import React, { Suspense } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";

// Common Components
import Header from "../src/components/CommonHeader";
import Footer from "../src/components/CommonFooter";
import Chatbot from "./pages/Chatbot";

// Main Pages
import HomePage from "../src/pages/HomePage";
import RegisterTeamsPage from "./pages/RegisterTeamsPage";
import PartnershipsPage from "./pages/PartnershipsPage";
import CareersPage from "./pages/CareersPage";
import VolunteerPage from "./pages/Volunteer";
import SeminarsWorkshops from "./pages/SeminarsWorkshops";
import SeminarDetailsPage from "./pages/SeminarDetail";
import PartnerWithUs from "./pages/PartnerWithUs";
import ComingSoonPage from "./pages/ComingSoonPage";

// Profile pages
import ProfilePage from "./pages/profile-pages/ProfilePage";
import ChatPage from "./pages/profile-pages/ChatPage";

// Authentication Pages
import SignUpPage from "./pages/SignUpPage";
import SignInPage from "./pages/SignInPage";

// About Us Pages
import AboutUsPage from "./pages/about-pages/AboutPage";
import ProblemWeAreSolvingPage from "./pages/about-pages/ProblemsWeAreSolvingPage";
import OurImpactPage from "./pages/about-pages/OurImpactPage";
import GoalObjectivesPage from "./pages/about-pages/GoalsObjectivesPage";
import FocusAreasAge from "./pages/about-pages/FocusAreasAge";

// Sports Pages
import CricketAbout from "./pages/sports-pages/CricketAbout";
import FootballAbout from "./pages/sports-pages/FootballAbout";
import JiuJitsuAbout from "./pages/sports-pages/JiuJitsuAbout";

// Event Pages
import BloomDayPage from "./pages/event-pages/BloomDayPage";
import HeroesDayPage from "./pages/event-pages/HeroesDayPage";
import OpenEventsPage from "./pages/event-pages/OpenEventsPage";
import SponsorEventPage from "./pages/EventSponserPage";

// Media Pages
import GalleryPage from "./pages/meida-pages/Gallery";
import ViewGallery from "./pages/meida-pages/ViewGallery";
import VideoHub from "./pages/meida-pages/VideoHub";
import NewsPage from "./pages/news-pages/NewsPage";

// News Detail Pages
import BloomNewsDetailPage from "./pages/news-pages/BloomNewsDetail";
import HeroesNewsDetailPage from "./pages/news-pages/HeroesNewsDetail";
import JiuJitsuNewsDetailPage from "./pages/news-pages/JiuJitsuNewsDetail";
import OpenEventNewsDetailPage from "./pages/news-pages/OpenEventsNewsDetail";

// Terms and Policy Pages
import ContactUsPage from "./pages/terms-and-policy-pages/ContactUs";
import AppPrivacyPolicy from "./pages/terms-and-policy-pages/AppPrivacyPolicy";
import AppTermsConditions from "./pages/terms-and-policy-pages/AppTermsConditions";
import AppDeleteAccount from "./pages/terms-and-policy-pages/AppDeleteAccount";
import CookiePolicyPage from "./pages/terms-and-policy-pages/CookiePolicy";
import PrivacyPolicyPage from "./pages/terms-and-policy-pages/PrivacyPolicy";
import TermsOfUsePage from "./pages/terms-and-policy-pages/TermOfUse";

// Forms
import ContactForm from "./components/ContactForm";
import ApplicationPortal from "./components/ApplicationPortal";

// Public match pages, reached from a shared match link. These deliberately
// render OUTSIDE the site chrome — see SiteLayout below.
import JoinMatchPage from "./pages/match-pages/JoinMatchPage";
import LiveMatchPage from "./pages/match-pages/LiveMatchPage";
import ReferralPage from "./pages/match-pages/ReferralPage";

// The YNO app landing page (app.nellab.org). Lazy, so the main site never
// downloads its code or stylesheet.
import { isAppSite } from "./lib/siteMode";
const YnoLanding = React.lazy(() => import("./pages/yno-landing/YnoLanding"));

// Placeholder Pages (for future development)
const ShopPage = () => <div>Shop Page Content</div>;
const StatsZonePage = () => <div>Stats Zone Page Content</div>;
const AppPage = () => <div>App Page Content</div>;
const NotFoundPage = () => <div>404 - Page Not Found</div>;

// Everything except the match pages: the marketing site, with its header,
// footer and chatbot.
const SiteLayout = () => {
  return (
    <>
      <Header />
      <main
        style={{
          minHeight: "60vh",
          borderTop: "2px solid #e0e0e0",
          paddingTop: "50px",
        }}
      >
        <Routes>
          {/* Main Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/register-teams" element={<RegisterTeamsPage />} />
          <Route path="/partnerships" element={<PartnershipsPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/volunteer" element={<VolunteerPage />} />
          <Route path="/seminars" element={<SeminarsWorkshops />} />
          <Route path="/seminar-detail" element={<SeminarDetailsPage />} />
          <Route path="/partner-with-us" element={<PartnerWithUs />} />

          {/* Form Routes */}
          <Route path="/contact-form" element={<ContactForm />} />
          <Route path="/job-application-form" element={<ApplicationPortal />} />

          {/* Profile and Chat Routes */}
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/chat" element={<ChatPage />} />

          {/* Generic Coming Soon Route - Keep as fallback */}
          <Route
            path="/coming-soon"
            element={<ComingSoonPage pageTitle="Content" />}
          />

          {/* Specific Coming Soon Routes with Props */}
          <Route
            path="/coming-soon/upcoming-events"
            element={<ComingSoonPage pageTitle="Upcoming Events Calendar" />}
          />
          <Route
            path="/coming-soon/host-event"
            element={<ComingSoonPage pageTitle="Host an Event" />}
          />
          <Route
            path="/coming-soon/gallery"
            element={<ComingSoonPage pageTitle="Gallery" />}
          />
          <Route
            path="/coming-soon/video-hub"
            element={<ComingSoonPage pageTitle="Video Hub" />}
          />
          <Route
            path="/coming-soon/magazine"
            element={<ComingSoonPage pageTitle="YNO Magazine" />}
          />
          <Route
            path="/coming-soon/press-releases"
            element={<ComingSoonPage pageTitle="Press Releases" />}
          />
          <Route
            path="/coming-soon/stats-zone"
            element={<ComingSoonPage pageTitle="Stats Zone" />}
          />
          <Route
            path="/coming-soon/shop"
            element={<ComingSoonPage pageTitle="Shop" />}
          />
          <Route
            path="/coming-soon/app"
            element={<ComingSoonPage pageTitle="App" />}
          />

          <Route
            path="/coming-soon/announcements-news"
            element={<ComingSoonPage pageTitle="Announcements & News" />}
          />

          {/* Authentication Routes */}
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/login" element={<SignInPage />} />

          {/* About Us Routes */}
          <Route path="/about-us" element={<AboutUsPage />} />
          <Route
            path="/problem-we-are-solving"
            element={<ProblemWeAreSolvingPage />}
          />
          <Route path="/our-impact" element={<OurImpactPage />} />
          <Route path="/goals-objectives" element={<GoalObjectivesPage />} />
          <Route path="/focus-areas-age" element={<FocusAreasAge />} />

          {/* Sports Routes */}
          <Route path="/football" element={<FootballAbout />} />
          <Route path="/cricket" element={<CricketAbout />} />
          <Route path="/jiujitsu" element={<JiuJitsuAbout />} />

          {/* Event Routes */}
          <Route path="/bloom-day" element={<BloomDayPage />} />
          <Route path="/heroes-day" element={<HeroesDayPage />} />
          <Route path="/open-events" element={<OpenEventsPage />} />
          <Route path="/sponsor-event" element={<SponsorEventPage />} />

          {/* Media Routes */}
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/view-gallery" element={<ViewGallery />} />
          <Route path="/video-hub" element={<VideoHub />} />
          <Route path="/news" element={<NewsPage />} />

          {/* News Detail Routes */}
          <Route path="/news/bloom-day" element={<BloomNewsDetailPage />} />
          <Route path="/news/heroes-day" element={<HeroesNewsDetailPage />} />
          <Route path="/news/jiu-jitsu" element={<JiuJitsuNewsDetailPage />} />
          <Route
            path="/news/open-events"
            element={<OpenEventNewsDetailPage />}
          />

          {/* Terms and Policy Routes */}
          <Route path="/contact" element={<ContactUsPage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-and-conditions" element={<TermsOfUsePage />} />


          {/* Get Involved Routes */}
          <Route path="/get-involved/player" element={<RegisterTeamsPage />} />
          <Route path="/get-involved/volunteer" element={<VolunteerPage />} />
          <Route path="/get-involved/partner" element={<PartnerWithUs />} />
          <Route path="/get-involved/sponsor" element={<SponsorEventPage />} />

          {/* Products Routes */}
          <Route path="/products/shop" element={<ShopPage />} />
          <Route path="/products/app" element={<AppPage />} />

          {/* Events Sub-routes */}
          <Route
            path="/events/calendar"
            element={<ComingSoonPage pageTitle="Upcoming Events Calendar" />}
          />
          <Route
            path="/events/host"
            element={<ComingSoonPage pageTitle="Host an Event" />}
          />
          <Route path="/events/workshops" element={<SeminarsWorkshops />} />

          {/* Media Sub-routes */}
          <Route
            path="/media/magzine"
            element={<ComingSoonPage pageTitle="YNO Magazine" />}
          />
          <Route path="/media/announcements" element={<NewsPage />} />
          <Route
            path="/media/press-releases"
            element={<ComingSoonPage pageTitle="Press Releases" />}
          />

          {/* User Dashboard Sub-routes */}
          <Route path="/user/profile" element={<ProfilePage />} />
          <Route path="/user/chat" element={<ChatPage />} />
          <Route path="/user/applications" element={<ProfilePage />} />

          {/* Stats Zone Route */}
          <Route path="/stats-zone" element={<StatsZonePage />} />

          {/* 404 Not Found Route - Must be last */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <Footer />

      <Chatbot />
    </>
  );
};

const App = () => {
  // app.nellab.org is one self-contained page with its own look: no router, no
  // site header, footer or chatbot.
  if (isAppSite()) {
    return (
      <Suspense fallback={null}>
        <YnoLanding />
      </Suspense>
    );
  }

  return (
    <Router>
      <Routes>
        {/* Public match pages. Standalone on purpose: they carry the YNO app's
            dark brand, so the site's header, footer and chatbot would fight
            them — and someone opening a shared match link wants the match, not
            the marketing site. */}
        <Route path="/join" element={<JoinMatchPage />} />
        <Route path="/join/:code" element={<JoinMatchPage />} />
        <Route path="/live" element={<LiveMatchPage />} />

        {/* Referral links shared from the app: nellab.org/r/<code>.
            On a phone with YNO installed Android never loads this — the URL
            matches the app's App Link intent filter and opens YNO directly.
            This page is what everyone else sees, and its job is to carry the
            code into the Play Store as `referrer=` so the install keeps it.
            Both shapes are routed: the bare /r is what a mangled forward
            degrades into, and the page says so rather than 404ing. */}
        <Route path="/r" element={<ReferralPage />} />
        <Route path="/r/:code" element={<ReferralPage />} />

        {/* The app's legal pages. Standalone for the same reason as the match
            pages: they are opened from inside the app and by app-store
            reviewers, where the marketing site's header, footer and chatbot are
            noise around the document they came to read. These URLs are
            submitted to the stores, so they must stay stable. */}
        <Route path="/app/privacy" element={<AppPrivacyPolicy />} />
        <Route path="/app/terms" element={<AppTermsConditions />} />
        <Route path="/app/delete-account" element={<AppDeleteAccount />} />

        {/* The home page of nellab.org is the YNO app landing page, standalone
            like app.nellab.org. Every other path keeps the site chrome. */}
        <Route
          path="/"
          element={
            <Suspense fallback={null}>
              <YnoLanding />
            </Suspense>
          }
        />

        {/* Everything else keeps the site chrome. */}
        <Route path="*" element={<SiteLayout />} />
      </Routes>
    </Router>
  );
};

export default App;